import { Timestamp } from "firebase/firestore";

const getTimestampCacheVersion = (timestamp: Timestamp): string => `${timestamp.seconds}.${timestamp.nanoseconds}`;

/**
 * Generate a cache key for Firebase Storage download URLs.
 *
 * Includes updatedAt timestamp to invalidate on re-uploads.
 * Firebase rotates download tokens on file overwrite, so we need a version indicator that changes.
 *
 * @param storagePath - Firebase Storage path to the file
 * @param updatedAt - Optional timestamp from the entry's updatedAt field
 * @returns Cache key string for download URL cache
 *
 * @example
 * const key = getDownloadUrlCacheKey("path/to/image.jpg", entry.updatedAt);
 * // Returns: "path/to/image.jpg|1234567890"
 */
export function getDownloadUrlCacheKey(storagePath: string, updatedAt?: Timestamp): string {
  return updatedAt ? `${storagePath}|${getTimestampCacheVersion(updatedAt)}` : storagePath;
}

export function getAnnotationMetadataCacheKey(metadataPath: string, updatedAt?: Timestamp): string {
  return updatedAt ? `${metadataPath}|${getTimestampCacheVersion(updatedAt)}` : metadataPath;
}

/**
 * Generate a cache key for annotated thumbnail blob URLs.
 *
 * Includes updatedAt timestamp to invalidate on re-uploads.
 * This ensures that when an entry is updated (including image re-uploads),
 * the cached annotated thumbnail is regenerated.
 *
 * @param imageUrl - Firebase Storage path to the original image
 * @param metadataPath - Firebase Storage path to the annotation metadata JSON
 * @param updatedAt - Optional timestamp from the entry's updatedAt field
 * @returns Cache key string for annotated thumbnail cache
 *
 * @example
 * const key = getAnnotatedThumbnailCacheKey(
 *   "path/to/image.jpg",
 *   "path/to/metadata.json",
 *   entry.updatedAt
 * );
 * // Returns: "path/to/image.jpg|path/to/metadata.json|1234567890"
 */
export function getAnnotatedThumbnailCacheKey(
  imageUrl: string,
  metadataPath: string,
  updatedAt?: Timestamp,
  annotationClassNames?: readonly string[],
  annotationClassColorOrder?: readonly string[]
): string {
  const base = updatedAt
    ? `${imageUrl}|${metadataPath}|${getTimestampCacheVersion(updatedAt)}`
    : `${imageUrl}|${metadataPath}`;
  const classKey = annotationClassNames?.length ? `|classes:${JSON.stringify(annotationClassNames)}` : "";
  const colorOrderKey = annotationClassColorOrder?.length
    ? `|class-colors:${JSON.stringify(annotationClassColorOrder)}`
    : "";
  return `${base}${classKey}${colorOrderKey}`;
}
